import { SwapBadge } from "@/components/organisms/SolutionView/SolutionPlayerCard";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import { DoneCheckbox } from "@/design-system/DoneCheckbox/DoneCheckbox";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import PlayerCardCondensed, {
  playerCardCondensedSize,
} from "@/design-system/PlayerCard/PlayerCardCondensed";
import { Skeleton } from "@/design-system/Skeleton/Skeleton";
import { pitchCardPlaceholder } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { PlayerSwapPopover } from "@/pages/Sbcs/components/PlayerSwapPopover";
import { SourceChip } from "@/pages/Sbcs/components/SourceChip";
import { useTranslation } from "@repo/shared/i18n";
import {
  StreamlineMarks,
  bracketSubtotal,
  isBracketDone,
  isMarked,
  slotOptions,
  stackEntries,
} from "@repo/shared/streamline";
import {
  StreamlineBracket,
  StreamlineOptions,
  StreamlinePlayerEntry,
  StreamlinePlayerOption,
  StreamlineSolution,
} from "@repo/shared/types";
import { ChevronDown } from "lucide-react";

import { StackLabels } from "./StackLabels";
import { StreamlinePlayerFacts } from "./StreamlinePlayerFacts";

/* Matches the handoff's ~106x146 card. */
const CARD_SCALE = 0.65;
/* The card's box; each card's column takes it so the chip below cannot outgrow the card. */
const CARD_SIZE = playerCardCondensedSize(CARD_SCALE);
/* A loading row stands in for a solution of unknown shape with this many cards. */
const SKELETON_CARDS = 3;

/**
 * Zebra background, the same device the compare and My Club tables use — with
 * rows this tall a hairline is not enough to tell one requirement from the next.
 */
const zebra = (index: number) =>
  index % 2 ? "bg-streamline-row-alt" : "bg-neutral-800";

/*
  A stack is the card, its chip, and the count beside them in one tinted box,
  so the count is plainly the card's. The top and left padding are paid back
  with a negative margin, so the card sits exactly where a single card would;
  the box only grows right and down, so neighbouring boxes keep the row's gap.
*/
const stackBox = (stack: boolean) =>
  cn(
    "flex items-start gap-2",
    stack &&
      "-mt-1.5 -ml-1.5 rounded-xl bg-white/5 p-1.5 pr-2.5 ring-1 ring-white/10",
  );

/*
  The caret at the head of the row, as GitHub's file headers: down while the
  cards show, turned right once they are folded away.
*/
const Caret = ({
  collapsed,
  onClick,
  className,
}: {
  collapsed: boolean;
  onClick?: () => void;
  className?: string;
}) => {
  const { t } = useTranslation("sbcs");
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={!collapsed}
      aria-label={t(
        collapsed ? "streamline.expandRow" : "streamline.collapseRow",
      )}
      className={cn(
        "-ml-1 flex size-6 shrink-0 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-white/10 hover:text-white",
        className,
      )}
    >
      <ChevronDown
        size={16}
        className={cn(
          "transition-transform duration-200",
          collapsed && "-rotate-90",
        )}
      />
    </button>
  );
};

/**
 * A row while a solve is in flight, on first open and on a re-solve alike:
 * the header's figures as skeletons and three of the pitch's placeholder
 * cards, each a beat behind the last so the pulse runs along the row. With
 * `added`, the row is one the added cards live in: its points are known,
 * the added cards stay where they are and the placeholders queue after them.
 */
export const StreamlineBracketRowSkeleton = ({
  index,
  scroll = false,
  added = [],
  points,
}: {
  index: number;
  scroll?: boolean;
  added?: StreamlinePlayerEntry[];
  points?: number;
}) => {
  return (
    <div className={cn("pt-4 pb-3.5", zebra(index), !scroll && "px-6.5")}>
      <div
        className={cn(
          "flex items-center gap-2.5",
          scroll ? "px-4.5 pb-2.5" : "pb-2.5",
        )}
      >
        <Caret collapsed={false} className="pointer-events-none opacity-50" />
        <span className="flex items-center gap-1.5 rounded-lg border border-streamline-points-border bg-streamline-points-surface px-2.5 -mr-1 py-1">
          <ItemScoreDiamond size={16} />
          <span className="text-base font-extrabold text-streamline-points">
            {points === undefined ? (
              <Skeleton className="w-8" />
            ) : (
              <span>{points.toLocaleString()}</span>
            )}
          </span>
        </span>
        <span className="text-lg font-extrabold text-white">
          <Skeleton className="w-8" />
        </span>
        {!scroll && (
          <span className="text-sm text-neutral-500">
            <Skeleton className="w-16" />
          </span>
        )}
        <span className="ml-auto pointer-events-none opacity-50">
          <DoneCheckbox checked={false} onChange={() => undefined} />
        </span>
      </div>
      <div
        className={cn(
          "flex gap-x-2.5",
          scroll
            ? "overflow-x-auto flex-nowrap px-4.5 pb-3 pt-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            : "flex-wrap",
        )}
      >
        {stackEntries(added).map(({ key, entries: [entry, ...rest] }) => (
          <div
            key={key}
            className={cn(stackBox(rest.length > 0), "opacity-50")}
          >
            <div
              style={{ width: CARD_SIZE.width }}
              className="flex flex-col items-center gap-1"
            >
              <PlayerCardCondensed
                player={entry.player}
                position={entry.position}
                scale={CARD_SCALE}
                untradeable={entry.untradeable}
              />
              <SourceChip
                source={entry.source}
                checked
                onToggle={() => undefined}
                className="pointer-events-none"
              />
            </div>
            {rest.length > 0 && (
              <StackLabels
                completed={rest.length + 1}
                total={rest.length + 1}
              />
            )}
          </div>
        ))}
        {Array.from({ length: SKELETON_CARDS }, (_, card) => (
          <div
            key={card}
            style={{ ...CARD_SIZE, animationDelay: `${card * 80}ms` }}
            className="flex shrink-0 items-center justify-center animate-pulse text-dark-disabled"
          >
            <div className="w-[88%]">{pitchCardPlaceholder}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * One requirement row: how many players of what points value, the Done control,
 * and the cards themselves. Each card opens the same swap popover the pitch
 * uses; its chip marks it done, which locks it. The caret folds the cards
 * away under the header; the page folds a row the user has just finished.
 *
 * Copies of one card are one stack: a card with a "7 / 20" label and a
 * stepper that marks a copy at a time, in row order. Its chip marks every
 * copy; a swap replaces the copies not yet marked, so the marked ones stay
 * as their own stack.
 */
export const StreamlineBracketRow = ({
  bracket,
  index,
  solution,
  solutions,
  options,
  optionsLoading = false,
  marks,
  onToggleBracket,
  onToggleEntry,
  onToggleEntries,
  onSwapPlayer,
  collapsed = false,
  onToggleCollapsed,
  scroll = false,
  loading = false,
}: {
  bracket: StreamlineBracket;
  /** Position in the list — drives the zebra stripe, nothing else. */
  index: number;
  /** The solution on screen, swaps folded in: what each slot's offer is derived from. */
  solution: StreamlineSolution;
  /** The set's solutions before any swaps; the solver's picks join the offer. */
  solutions: StreamlineSolution[];
  options: StreamlineOptions;
  /** The options are still on their way: the badge on every card turns. */
  optionsLoading?: boolean;
  marks: StreamlineMarks;
  onToggleBracket: () => void;
  onToggleEntry: (entryId: string) => void;
  /** Several cards in one go, as a stack's chip marks every copy. */
  onToggleEntries: (entryIds: string[]) => void;
  onSwapPlayer: (entryId: string, option: StreamlinePlayerOption) => void;
  /** Only the header shows; the cards are folded away under it. */
  collapsed?: boolean;
  onToggleCollapsed: () => void;
  /** Mobile: cards scroll sideways instead of wrapping. */
  scroll?: boolean;
  /** A re-solve is in flight: the row gives way to its skeleton. */
  loading?: boolean;
}) => {
  const { t } = useTranslation("sbcs");
  const done = isBracketDone(bracket, marks);

  /* Mid-solve only the added cards are marked: they hold their row while
     the solver's cards give way to placeholders. */
  if (loading) {
    const added = bracket.players.filter((entry) =>
      isMarked(marks, entry.entryId),
    );
    return (
      <StreamlineBracketRowSkeleton
        index={index}
        scroll={scroll}
        added={added}
        points={added.length ? bracket.points : undefined}
      />
    );
  }

  return (
    <div className={cn("pt-4 pb-3.5", zebra(index), !scroll && "px-6.5")}>
      <div
        className={cn(
          "flex items-center gap-2.5",
          scroll ? "px-4.5 pb-2.5" : "pb-2.5",
        )}
      >
        <Caret collapsed={collapsed} onClick={onToggleCollapsed} />
        <span className="flex items-center gap-1.5 rounded-lg border border-streamline-points-border bg-streamline-points-surface px-2.5 -mr-1 py-1">
          <ItemScoreDiamond size={16} />
          <span className="text-base font-extrabold text-streamline-points">
            {bracket.points.toLocaleString()}
          </span>
        </span>
        <span className="text-lg font-extrabold text-white">
          {`x ${bracket.players.length}`}
        </span>
        {/* Desktop only: the phone's header has no room for the subtotal. */}
        {!scroll && (
          <span className="text-sm text-neutral-500">
            {t("streamline.pointsSubtotal", {
              points: bracketSubtotal(bracket).toLocaleString(),
            })}
          </span>
        )}
        <span className="ml-auto">
          <DoneCheckbox checked={done} onChange={onToggleBracket} />
        </span>
      </div>
      <Collapsible open={!collapsed}>
        <div
          className={cn(
            "flex gap-x-2.5 gap-y-3",
            scroll
              ? "overflow-x-auto flex-nowrap px-4.5 pb-3 pt-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              : "flex-wrap",
          )}
        >
          {stackEntries(bracket.players).map(({ key, entries }) => {
            const stack = entries.length > 1;
            const completed = entries.filter((e) => isMarked(marks, e.entryId));
            const open = entries.filter((e) => !isMarked(marks, e.entryId));
            const [entry] = entries;
            const marked = open.length === 0;
            /* Nothing is offered until the options land: the solver's own
                 cards alone make a thin, odd offer, and the popover would
                 change under the user when the rest arrived. A stack is
                 offered market cards only: the club rarely has a spare copy
                 for every slot in it. */
            const swaps =
              marked || optionsLoading
                ? []
                : slotOptions(
                    solution,
                    open[0] ?? entry,
                    options,
                    solutions,
                  ).filter((option) => !stack || option.source === "MARKET");
            /* Every card shows its market value; the chip below says
                 whether it has to be bought. */
            const card = (
              <div className="relative">
                <PlayerCardCondensed
                  player={entry.player}
                  position={entry.position}
                  scale={CARD_SCALE}
                  untradeable={entry.untradeable}
                />
                {!marked && (optionsLoading || swaps.length > 0) && (
                  <SwapBadge loading={optionsLoading} size={16} />
                )}
              </div>
            );
            return (
              <div key={key} className={stackBox(stack)}>
                <div
                  style={{ width: CARD_SIZE.width }}
                  className="flex flex-col items-center gap-1"
                >
                  {marked ? (
                    /* Completed: locked until its chip is unchecked again. */
                    <div className="opacity-50">{card}</div>
                  ) : (
                    <PlayerSwapPopover
                      facts={
                        <StreamlinePlayerFacts
                          player={entry.player}
                          position={entry.position}
                          source={entry.source}
                          untradeable={entry.untradeable}
                        />
                      }
                      options={swaps}
                      onSelect={(swapIndex) =>
                        open.forEach((e) =>
                          onSwapPlayer(e.entryId, swaps[swapIndex]),
                        )
                      }
                    >
                      {card}
                    </PlayerSwapPopover>
                  )}
                  <SourceChip
                    source={entry.source}
                    checked={marked}
                    /* One card toggles; a stack goes all the way: every open
                       copy marked, or every marked copy cleared. */
                    onToggle={() =>
                      onToggleEntries(
                        (marked ? completed : open).map((e) => e.entryId),
                      )
                    }
                  />
                </div>
                {stack && (
                  <StackLabels
                    completed={completed.length}
                    total={entries.length}
                    onAdd={() => open[0] && onToggleEntry(open[0].entryId)}
                    onRemove={() =>
                      completed[completed.length - 1] &&
                      onToggleEntry(completed[completed.length - 1].entryId)
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      </Collapsible>
    </div>
  );
};
